import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { TopReservationVehicleDashboardDto } from 'api/dashboard/dtos/TopReservationVehicleDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useInfiniteReveal from '../components/useInfiniteReveal';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import styles from '../monthlyKmRanking/MonthlyKmRanking.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a',
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0',
];

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const TopReservationVehicles = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<TopReservationVehicleDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('topReservationVehicles');

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo };
				const result = await DashboardService.getTopReservationVehicles(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const prevCriteria = props.filters
					? { ...props.filters, dateFrom: range.prevFrom, dateTo: range.prevTo }
					: { dateFrom: range.prevFrom, dateTo: range.prevTo };
				const previous = await DashboardService.getTopReservationVehicles(prevCriteria);
				if (!cancelled) {
					setPreviousTotal((previous || []).reduce((s, x) => s + x.reservationsCount, 0));
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const items = data || [];
	const maxCount = Math.max(...items.map(d => d.reservationsCount), 0);
	const totalCount = items.reduce((sum, item) => sum + item.reservationsCount, 0);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	const buildVehicleTooltip = (item: TopReservationVehicleDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.top_reservation_vehicles.title' as any) as string}
			subTitleTooltip={t('dashboard.top_reservation_vehicles.tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : !data || data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={String(totalCount)}
						subLabel={t('dashboard.total_reservations' as any) as string}
						currentTotal={totalCount}
						previousTotal={previousTotal}
						previousRange={previousRange}
						lowerIsBetter={false}
					/>
					<div className={styles.list}>
					{visibleItems.map((item, index) => {
						const barWidth = maxCount > 0 ? (item.reservationsCount / maxCount) * 100 : 0;
						const pct = totalCount > 0 ? Math.round((item.reservationsCount / totalCount) * 100) : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.vehicleId}
								className={styles.row}
								onClick={() => window.open('/reservations', '_blank')}
							>
								<span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="top-reservation-vehicles-tooltip" data-place="right">{item.registrationNumber}</span></span>
								<div className={styles.barTrack}>
									<div className={styles.barFill} style={{ width: `${barWidth}%`, background: color }} />
								</div>
								<span className={styles.km}>
									{item.reservationsCount}
								</span>
								<span className={styles.pct}>{pct}%</span>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
					<ReactTooltip id="top-reservation-vehicles-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default TopReservationVehicles;
